<template>
  <v-container
    id="property-settings-list"
    :key="containerKey"
  >
    <report-timezone-modal ref="reportModal" />
    <valkyrie-list
      ref="valkyrieList"
      list-name="userProperties"
      :pagination-text="paginationText"
      :list-data="listData"
      :pagination="userPropertyPagination"
      :headers="headers"
      :is-loading="loading"
      @update-pagination="handleUpdatePagination"
    >
      <template v-slot:list-header>
        <property-settings-list-header
          :pagination-text="paginationText"
          :searchable="hasDataToQuery"
          :filterable="hasDataToQuery"
          @search="handleUpdateSearch"
          @clear-filters="handleClearFilters"
        >
          <default-property-settings />
          <template v-if="editable">
            <div
              class="my-auto ml-2"
              @click="checkAdmin"
            >
              <v-btn
                class="icon-btn"
                elevation="1"
                color="primary"
                :disabled="disableEditing"
                @click="$emit('toggle-edit-permissions')"
              >
                <font-awesome-icon :icon="['fas', 'home']" />
                Edit Permissions
              </v-btn>
            </div>
          </template>
          <template #tags>
            <async-multiselect-filter
              filter-key="tag_id"
              :searchable="false"
              :fetch-function="fetchAllPropertyTags"
              @apply-multiselect="handleUpdateFilters"
            />
          </template>
        </property-settings-list-header>
      </template>
      <!-- eslint-disable vue/valid-v-slot -->
      <template #header.notificationDisconnect="{ header }">
        <span class="small-header">{{ header.text }}</span>
      </template>
      <template #header.notificationActionableAlert="{ header }">
        <span class="small-header">{{ header.text }}</span>
      </template>
      <template #header.notificationOccupancy="{header}">
        <span
          class="small-header"
        ><div>{{ header.text }}</div></span>
      </template>
      <template #header.reports="{header}">
        <div

          style="text-align: center"
        >
          {{ header.text }}
          <v-icon
            id="reports"
            size="14"
          >
            $faInfoCircle
          </v-icon>
          <div
            v-ripple
            style="cursor: pointer; font-size: 12px; border: 1px solid grey; border-radius: 7px"
            @click="handleBulkReportToggle()"
          >
            Enable for all properties
          </div>
        </div>
        <v-tooltip
          activator="#reports"
          bottom
          content-class="property-settings-tooltip"
        >
          <span>Daily Reports are sent every day at 4 AM</span>
        </v-tooltip>
      </template>
      <template #item.propertyName="{item}">
        <router-link
          :to="{ name: 'Property Details', params: {id: `${item.propertyID}` } }"
          class="property-name"
        >
          {{ item.property.name }}
        </router-link>
      </template>
      <template #item.tags="{item}">
        <v-chip
          v-for="(tag, idx) in item.property.tags"
          :key="idx"
          outlined
          color="#535353"
          class="ma-1 tag"
        >
          {{ tag }}
        </v-chip>
      </template>
      <template #item.alerts="{ item }">
        <div class="alert-icon-container">
          <v-tooltip
            content-class="property-settings-tooltip"
            bottom
            max-width="300px"
            open-delay="350"
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
              >
                <v-simple-checkbox
                  v-model="item.notificationActionableAlert"
                  class="alert-icon-toggle"
                  on-icon="$faSignalStream"
                  off-icon="$faSignalStream"
                  :disabled="disableEditing"
                  :ripple="false"
                  color="success"
                  @click="checkAdmin()"
                  @input="updateUserPropertySettings(item)"
                />
              </div>
            </template>
            <span>{{ item.notificationActionableAlert ? 'Disable' : 'Enable' }} Noise Alerts</span>
          </v-tooltip>
          <v-tooltip
            content-class="property-settings-tooltip"
            bottom
            max-width="300px"
            open-delay="350"
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
              >
                <v-simple-checkbox
                  v-model="item.notificationDisconnect"
                  class="alert-icon-toggle"
                  on-icon="$faWifiSlashSolid"
                  off-icon="$faWifiSlashSolid"
                  :disabled="disableEditing"
                  :ripple="false"
                  color="success"
                  @click="checkAdmin()"
                  @input="updateUserPropertySettings(item)"
                />
              </div>
            </template>
            <span>{{ item.notificationDisconnect ? 'Disable' : 'Enable' }} Disconnect Alerts</span>
          </v-tooltip>
          <v-tooltip
            content-class="property-settings-tooltip"
            bottom
            max-width="322px"
            open-delay="350"
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
              >
                <v-simple-checkbox
                  v-model="item.notificationOccupancy"
                  class="alert-icon-toggle"
                  on-icon="$faUsers"
                  off-icon="$faUsers"
                  :disabled="(item.property.propertyType == 'Apartment/Condo' || item.property.propertyType == 'Apartment/Condo Sensitive') || disableEditing"
                  :ripple="false"
                  color="success"
                  @click="checkAdmin()"
                  @input="updateUserPropertySettings(item)"
                />
              </div>
            </template>
            <span v-if="(item.property.propertyType == 'Apartment/Condo' || item.property.propertyType == 'Apartment/Condo Sensitive')">
              CrowdControl is only available for Houses
            </span>
            <span v-else>
              {{ item.notificationOccupancy ? 'Disable' : 'Enable' }} Crowd Control Alerts
            </span>
          </v-tooltip>
        </div>
      </template>
      <template #item.reports="{item}">
        <v-tooltip
          content-class="property-settings-tooltip"
          bottom
          max-width="300px"
          open-delay="350"
        >
          <template v-slot:activator="{ on, attrs }">
            <center
              v-bind="attrs"
              v-on="on"
            >
              <v-simple-checkbox
                :disabled="reportTogglesLoading.includes(item.propertyID)"
                :value="item.reportInStay"
                class="report-icon-toggle"
                on-icon="$faFileChartLine"
                off-icon="$faFileChartLine"
                :ripple="false"
                color="success"
                @input="handleReportToggle('inStay', item.propertyID, item.organizationID, { inStay: item.reportInStay, maintenance: item.reportMaintenance })"
              />
            </center>
          </template>
          <span>{{ item.reportInStay ? 'Disable' : 'Enable' }} ResolutionReport</span>
        </v-tooltip>
      </template>
      <template slot="empty-state">
        <empty-state
          icon="$faHomeLight"
          text="No properties found"
        />
      </template>
    </valkyrie-list>
  </v-container>
</template>

<script>
  import { mapGetters } from 'vuex'
  import ValkyrieList from '@/components/ValkyrieList'
  import EmptyState from '@/components/base/EmptyState'
  import PropertySettingsListHeader from './PropertySettingsListHeader.vue'
  import AsyncMultiselectFilter from '@/components/filters/AsyncMultiselectFilter.vue'
  import paginationHelper from '@/helpers/pagination-helper'
  import listControllerMixin from '@/helpers/list-controller-mixin'
  import { OCCUPANCY } from '@/helpers/feature-constants.js'
  import DefaultPropertySettings from '@/components/user/DefaultPropertySettings'
  import axios from 'axios'
  import ReportTimezoneModal from './ReportTimezoneModal.vue'

  export default {
    name: 'PropertySettingsList',
    components: { ValkyrieList, PropertySettingsListHeader, EmptyState, AsyncMultiselectFilter, DefaultPropertySettings, ReportTimezoneModal },

    mixins: [listControllerMixin],
    props: {
      userId: {
        type: String,
        default: '',
      },
      iconCard: {
        type: Boolean,
        default: false,
      },
      hasFilters: {
        type: Boolean,
        default: true,
      },
      filterBy: {
        type: String,
        default: '',
      },
      editable: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      hasDataToQuery: false,
      containerKey: 0,
      userList: true,
      selected: [],
      reportTogglesLoading: [],
    }),
    computed: {
      ...mapGetters({
        total: 'Users/userPropertyTotal',
        currentSelectedOrg: 'Permissions/currentSelectedOrg',
        userRoleOnOrg: 'Permissions/userRoleOnOrg',
        eligibleFeatures: 'Organizations/eligibleFeatures',
        isInternal: 'Permissions/isInternal',
        currentUser: 'Users/currentUser',
        isOrgOwner: 'Permissions/isOrgOwner',
        userTimezone: 'Users/userTimezone',
        currentUserOrgs: 'Users/currentUserOrgs',
      }),
      userOrgIds () {
        return this.currentUserOrgs.map(org => org?.id)
      },
      orgId () {
        return this.currentSelectedOrg.id
      },
      orgOccupancyEligible () {
        return this.eligibleFeatures.includes(OCCUPANCY)
      },
      headers () {
        const defaultHeaders = [
          {
            text: 'Name',
            value: 'propertyName',
          },
          {
            text: 'Address',
            value: 'property.street1',
          },
          {
            text: 'City',
            value: 'property.city',
          },
          {
            text: 'State',
            value: 'property.region',
          },
          {
            text: 'Tags',
            value: 'tags',
          },
          {
            text: 'Property Type',
            value: 'property.propertyType',
          },
          {
            text: 'Alerts',
            value: 'alerts',
          },
          {
            text: 'Daily Reports',
            value: 'reports',
            width: 175,
          },
        ]
        return defaultHeaders
      },
      disconnectCheckedCount () {
        let count = 0
        if (this.listData) {
          count = this.listData.filter((item) => item.notificationDisconnect === true).length
        }
        return count
      },
      noiseCheckedCount () {
        let count = 0
        if (this.listData) {
          count = this.listData.filter((item) => item.notificationActionableAlert === true).length
        }
        return count
      },
      occupancyCheckCount () {
        let count = 0
        if (this.listData) {
          count = this.listData.filter((item) => item.notificationOccupancy === true)
        }
        return count
      },
      paginationText () {
        return paginationHelper(this.pageNum, this.perPage, this.total)
      },
      userPropertyPagination () {
        return {
          ...this.pagination,
          total: this.total,
        }
      },
      staticFilters () {
        return [{ key: 'user_id', operator: '=', value: this.userId }]
      },
      listAPIPath () {
        return `${this.$store.state.baseURL}/user/notification/properties/list`
      },
      isOwnAccount () {
        return this.currentUser.id === parseInt(this.userId, 10)
      },
      disableEditing () {
        return !this.isOrgOwner && (this.userRoleOnOrg === 'Administrator' || this.userRoleOnOrg === 'Account Owner') && !this.isInternal && !this.isOwnAccount
      },
      needsToSetUserTimezone () {
        return this.userTimezone === undefined || this.userTimezone === null
      },
    },
    watch: {
      currentSelectedOrg (newId) {
        if (newId) this.initializePropertiesList()
      },
      userRoleOnOrg (newRole) {
        if (newRole) this.initializePropertiesList()
      },
      $route (to) {
        if (to.query.index) {
          this.initializePropertiesList()
          this.containerKey++
        }
      },
    },
    async created () {
      await this.initializePropertiesList()
    },
    beforeDestroy () {
      this.$root.$confirm.$close()
    },
    methods: {
      checkAdmin () {
        const accountType = (this.userRoleOnOrg === 'Account Owner') ? 'an owner account' : 'another administrator'
        if (this.disableEditing) this.$store.dispatch('updateMessages', { type: 'info', text: `You cannot edit ${accountType}` }, { root: true })
      },
      fetchAllPropertyTags (payload) {
        return this.$store.dispatch('Properties/fetchPropertyListFilters', { key: 'tags', ...this.filterPayload, ...payload })
      },
      async initializePropertiesList () {
        await this.fetchList().then((res) => {
          this.updateTotal(res.data.pagination.total)
          this.hasDataToQuery = res.data.pagination.total > 0
        })
      },
      updateTotal (total) {
        this.$store.commit('Users/updateUserPropertyTotal', total)
      },
      updateUserPropertySettings (item) {
        const payload = {
          userId: this.userId,
          body: {
            notificationOccupancy: item.notificationOccupancy,
            notificationActionableAlert: item.notificationActionableAlert,
            notificationDisconnect: item.notificationDisconnect,
            propertyID: item.propertyID,
            organizationID: item.organizationID,
          },
        }
        this.$store.dispatch('Users/updateUserPropertySettings', payload)
      },
      async handleBulkReportToggle () {
        this.reportTogglesLoading.push(this.listData.map(item => item.propertyID))
        if (this.needsToSetUserTimezone) {
          try {
            await this.$refs.reportModal.open()
            this.$refs.reportModal.close()
            await this.bulkReportToggle('inStay')
          } catch (e) {
            // user closed the modal without setting their timezone
          }
        } else {
          await this.bulkReportToggle('inStay')
        }
        await this.fetchList()
        this.reportTogglesLoading = []
      },
      async bulkReportToggle (reportType) {
        const reports = { inStay: false, maintenance: false }
        const payload = { orgIDs: this.userOrgIds, userID: Number(this.userId), ...reports, [reportType]: true }
        await axios.post(`${this.$store.state.baseURL}/reports/bulkreportsbyorg`, payload)
          .then((res) => {
            if (res.status === 200) {
              const payload = { type: 'success', text: 'Report settings updated!', unread: true }
              this.$store.dispatch('updateMessages', payload, { root: true })
            }
          })
      },
      async handleReportToggle (reportType, propertyID, organizationID, settings) {
        this.reportTogglesLoading.push(propertyID)
        if (this.needsToSetUserTimezone) {
          try {
            await this.$refs.reportModal.open()
            this.$refs.reportModal.close()
            await this.updateReportSetting(reportType, propertyID, organizationID, settings)
          } catch (e) {
            // user closed the modal without setting their timezone
          }
        } else {
          await this.updateReportSetting(reportType, propertyID, organizationID, settings)
        }
        await this.fetchList()
        this.reportTogglesLoading = this.reportTogglesLoading.filter(pID => propertyID !== pID)
      },
      async updateReportSetting (reportType, propertyID, organizationID, settings) {
        const payload = { ...settings, [reportType]: !settings[reportType], propertyID, orgId: organizationID, userID: Number(this.userId) }
        await axios.put(`${this.$store.state.baseURL}/reports/`, payload)
          .then((res) => {
            if (res.status === 200) {
              const payload = { type: 'success', text: 'Report settings updated!', unread: true }
              this.$store.dispatch('updateMessages', payload, { root: true })
            }
          })
          .catch(() => {
            const payload = { type: 'error', text: 'There was an error updating your settings. Please try again.', unread: true }
            this.$store.dispatch('updateMessages', payload, { root: true })
          })
      },
    },
  }
</script>

<style lang="scss">

#property-settings-list .toggle.v-input--selection-controls {
  display: flex;
  margin: 1px 0 12px 9px !important;
  justify-content: center;
}

.alert-icon-container {
  min-width: 120px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  & > * {
    flex-grow: 1;
    flex-basis: auto;
  }
  &  .alert-icon-toggle {
    & svg{
      width: 20px;
    }
  }
}
.property-settings-tooltip {
  opacity: 1 !important;
  background: #694361;
  padding: 0 8px;
  border-radius: 0;
}
.report-icon-toggle {
  & svg {
    width: 14px !important;
    margin: auto;
  }
}

.small-header {
  font-size: 12px;
  text-align: center;
  letter-spacing: 0.382511px;
  line-height:30px;
  color: #535353;
  line-height: 14px;
  display: block;
}
#property-settings-list {
  padding: 12px 0;
  .property-name {
    color: $muted;
    font-weight: 600;
    letter-spacing: 0.191002px;
    text-decoration: none;
  }
  .tag {
    border-color: #535353;
    .v-chip__content {
      font-size: 14px;
    }
  }
  .report-setting-label {
    font-family: 'Lato';
    font-style: normal;
    font-weight: 400;
    font-size: 15px;
    color: $na-medium-grey;
    white-space: nowrap;
  }
  .report-setting-value {
    font-family: 'Lato';
    font-style: normal;
    font-weight: 700;
    font-size: 15px;
    cursor: pointer;
    &.enabled {
      color: $success;
    }
  }
}

</style>
